import { getAccessToken } from '../auth/AuthProvider';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:5001';

export interface AppConfiguration {
  copilotStudioUrl: string;
  companyCode: string;
  lastUpdatedBy: string;
  lastUpdatedAt: string;
}

export interface ConnectionTestResult {
  isSuccessful: boolean;
  message: string;
  errorDetails?: string;
  agentName?: string;
  agentId?: string;
}

export interface ConfigurationHistory {
  key: string;
  oldValue: string;
  newValue: string;
  updatedBy: string;
  updatedAt: string;
}

class AdminService {
  /**
   * Get current configuration
   */
  async getConfiguration(): Promise<AppConfiguration> {
    const token = await getAccessToken();
    const response = await fetch(`${API_BASE_URL}/admin/config`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get configuration');
    }

    return await response.json();
  }

  /**
   * Update Copilot Studio URL
   */
  async updateCopilotStudioUrl(url: string): Promise<void> {
    const token = await getAccessToken();
    const response = await fetch(`${API_BASE_URL}/admin/config/copilot-url`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update configuration');
    }
  }

  /**
   * Get company code
   */
  async getCompanyCode(): Promise<string> {
    const token = await getAccessToken();
    const response = await fetch(`${API_BASE_URL}/admin/config/company`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get company code');
    }

    const data = await response.json();
    return data.companyCode;
  }

  /**
   * Update company code
   */
  async updateCompanyCode(companyCode: string): Promise<void> {
    const token = await getAccessToken();
    const response = await fetch(`${API_BASE_URL}/admin/config/company`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ companyCode }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update company code');
    }
  }

  /**
   * Test connection to a Copilot Studio URL
   */
  async testConnection(url: string): Promise<ConnectionTestResult> {
    const token = await getAccessToken();
    const response = await fetch(`${API_BASE_URL}/admin/test-connection`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url }),
    });

    if (!response.ok) {
      throw new Error('Failed to test connection');
    }

    return await response.json();
  }

  /**
   * Get configuration change history
   */
  async getConfigHistory(): Promise<ConfigurationHistory[]> {
    const token = await getAccessToken();
    const response = await fetch(`${API_BASE_URL}/admin/history`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get configuration history');
    }

    return await response.json();
  }
}

export const adminService = new AdminService();
